/**
 * Hotel offers utility functions
 * Date calculations, filtering, and snippet generation for hotel offers
 */

/** Item with optional expiration date */
export interface ExpiringItem {
  expirationDate?: string
  [key: string]: unknown
}

/** Hotel offer structure */
export interface FormattedHotelOffer {
  offer: {
    name: string
    type: 'FREE' | 'DISCOUNT' | string
  }
  discountValue?: number | null
  formattedDiscount?: string
}

/** Translation snippet for i18n query */
export interface TranslationSnippet {
  snippet: string
  variables?: string
}

/**
 * Calculate difference between two dates normalized to midnight
 * Ignores time component, compares only dates
 * @param date1 - First date to compare
 * @param date2 - Second date to compare
 * @returns Difference in milliseconds
 */
export function getDatesDifference(date1: Date, date2: Date): number {
  const d1 = new Date(date1.getFullYear(), date1.getMonth(), date1.getDate())
  const d2 = new Date(date2.getFullYear(), date2.getMonth(), date2.getDate())

  return d1.getTime() - d2.getTime()
}

/**
 * Filter items by expiration date
 * Keeps items without expiration or with future expiration
 * @param items - Array of items with optional expirationDate
 * @returns Filtered array containing only non-expired items
 */
export function filteringByExpirationDate<T extends ExpiringItem>(items: T[]): T[] {
  const now = new Date()

  return items?.filter(item => {
    if (!item.expirationDate) {
      return true
    }

    // Replace @ with T for ISO format compatibility
    const dateString = item.expirationDate.replace('@', 'T')
    const expirationDate = new Date(dateString)

    return expirationDate > now
  }) ?? []
}

/**
 * Generate translation snippets for hotel offers
 * Creates i18n query snippets based on offer type and discount
 * @param offers - Array of hotel offers
 * @returns Array of translation snippets for i18n fetching
 */
export function getSnippetsForQuery(offers: FormattedHotelOffer[]): TranslationSnippet[] {
  const snippets: TranslationSnippet[] = []

  for (let i = 0; i < offers.length; i++) {
    const offer = offers[i]
    snippets.push({ snippet: `ho_${offer.offer.name}_text` })

    if (offer.offer.type === 'FREE') {
      snippets.push({ snippet: `ho_${offer.offer.name}_title` })
    } else if (offer.offer.type === 'DISCOUNT' && offer.discountValue === null) {
      snippets.push({ snippet: `ho_${offer.offer.name}_free_title` })
    } else if (offer.offer.type === 'DISCOUNT' && offer.discountValue !== null) {
      snippets.push({
        snippet: `ho_${offer.offer.name}_discount_title`,
        variables: `{"DiscountPercent":"${offer.formattedDiscount}"}`,
      })
    }
  }

  return snippets
}

/**
 * Validate date string format and value
 * Checks if date string represents a valid date
 * @param dateValue - Date string in YYYY-MM-DD format or null
 * @returns True if date is valid or null
 */
export function isDateValid(dateValue: string | null): boolean {
  if (dateValue === null) return true

  const date = new Date(dateValue)

  // Check if Date object is valid
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return false
  }

  // Parse input components
  const [year, month, day] = dateValue.split('-').map(Number)

  // Compare with actual Date values
  const actualYear = date.getFullYear()
  const actualMonth = date.getMonth() + 1
  const actualDay = date.getDate()

  // Ensure no date component overflow (e.g., 2024-02-31 → 2024-03-03)
  return year === actualYear && month === actualMonth && day === actualDay
}